/**
 * constant.js
 *
 * This file includes all application-wide constant values. This may include enums, reference IDs or data. For example:
 *
 * - enum like PT_* pricing types
 * - product or targeting IDs for custom logic
 *
 * Specifically in __THIS__ file are all API-related constants referring to
 * specific model instances, enums, etc. Also any values required for model
 * validations, etc.
 */

// Rate Card Item - Rate Type
export const RT_COST_PLUS = 'COSTPLUS';

// Pricing Type enum values
export const PT_CPM = 'CPM';
export const PT_COST_PLUS = 'COST_PLUS';
export const PT_FIXED_MARGIN = 'FIXED_MARGIN';
export const PT_ADDED_VALUE = 'ADDED_VALUE';
export const PT_CPA = 'CPA';

// Creative Type enum values
export const CT_NORMAL = 'NORMAL';
export const CT_SEARCH = 'SEARCH';
export const CT_SOCIAL = 'SOCIAL';
export const CT_PAYPERCALL = 'PAY_PER_CALL';
export const CT_URL_ONLY = 'URL_ONLY';
export const CT_NATIVE_ARTICLE = 'NATIVE_ARTICLE';
export const CT_NATIVE_DISPLAY = 'NATIVE_DISPLAY';
export const CT_NATIVE_VIDEO = 'NATIVE_VIDEO';
export const CT_YOUTUBE_BUMPER_VIDEO = 'YOUTUBE_BUMPER_VIDEO';
export const CT_YOUTUBE_NONSKIPPABLE_VIDEO = 'YOUTUBE_NONSKIPPABLE_VIDEO';
export const CT_SOCIAL_TIKTOK = 'SOCIAL_TIKTOK';
export const CT_SOCIAL_SNAPCHAT = 'SOCIAL_SNAPCHAT';

export const ORDER_ATTACHMENT = -2;
// TODO: fix these const names with better prefixes (FILE_TYPE_*)
export const CREATIVE_FILE_SIZE_ALLOWED = 150; // 150KB
export const BYTES_IN_KB = 1024;
export const KBS_IN_MB = 1024;
export const MBS_IN_GB = 1024;
export const FILE_TYPE_IO_ATTACHMENT = 'IO_ATTACHMENT';
export const FILE_TYPE_NOTES = 'NOTES';
export const FILE_TYPE_CREATIVE = 'CREATIVE';
export const ARTICLE_FILE_TYPE_CREATIVE = 'ARTICLE_CREATIVE';
export const FILE_TYPE_COMPANION_ASSET = 'COMPANION_ASSET';
export const FILE_TYPE_CLOSED_CAPTIONING = 'CLOSED_CAPTIONING';
export const FILE_TYPE_TRACKING_PIXEL = 'TRACKING_PIXEL';
export const FILE_TRACKING_PIXEL = 6;
// file.type field
export const FILE_TYPE_UPLOAD = 'UPLOAD';
export const FILE_TYPE_LINK = 'LINK';
export const ARTICLE_UPLOAD = 'ARTICLE_UPLOAD';
export const ARTICLE_LINK = 'ARTICLE_LINK';
export const COMPANION_ASSET_UPLOAD = 'COMPANION_ASSET_UPLOAD';
export const COMPANION_ASSET_LINK = 'COMPANION_ASSET_LINK';
export const CLOSED_CAPTIONING_UPLOAD = 'CLOSED_CAPTIONING_UPLOAD';

// Media types
export const MT_SEARCH = 3;
export const MT_SOCIAL = 4;
export const MT_DISPLAY = 1;
export const MT_VIDEO = 2;
export const MT_AUDIO = 5;
export const MT_DIGITAL_OUT_OF_HOME = 6;
export const MT_OTT_BUNDLES = 7;

// Product types constant
export const PROD_PPC = 26;
export const PROD_PAYPERCALL = 26;
export const PROD_OTT = 5;
export const PROD_SEM = 7;
export const PROD_TRADITIONAL = 1;
export const PROD_GDN = 8;
export const PROD_CTV = 31;
export const PROD_OTT_EXTENDED_LOCAL_REACH = 32;
export const PROD_BLENDED_OTT_PRE_ROLL = 33;
export const PROD_PRE_ROLL = 34;
export const PROD_NATIVE_DISPLAY = 2;
export const PROD_NATIVE_VIDEO = 4;
export const PROD_NATIVE_ARTICLE = 55;
export const PROD_YOUTUBE_BUMPER = 64;
export const PROD_YOUTUBE_SKIPPABLE = 65;
export const PROD_YOUTUBE_NONSKIPPABLE = 66;
export const PROD_YOUTUBE_TV = 70;
export const PROD_TWITCH = 71;
export const PROD_TIKTOK = 72;
export const PROD_SNAPCHAT = 73;

// Targetable Element
export const TAR_INCOME = 2;
export const TAR_BEHAVIOURAL_SEGMENT = 4;
export const TAR_KEYWORD_TARGETING = 7;
export const TAR_CUSTOM_BEHAVIOURAL_SEGMENT = 5;
export const TAR_DEMOGRAPHIC_TARGETING = 28;
export const TAR_CUSTOM_XANDR_SEGMENT_ID = 32;
export const TAR_AUDIENCE_NOTES = 38;
export const TAR_CTV_RON_TARGETING = 29;
export const TAR_CONTEXTUAL_TARGETING = 41;
export const TAR_AUDIENCE_FIRST_PARTY_TARGETING = 6;

// Device types
export const DT_CTV = 3;

// Tracking Pixels
export const TP_VIEW_THROUGH_PIXEL = 2;
export const TP_CONVERSION_PIXEL = 3;
export const TP_CONVERSION_ZONE = 4;
export const TP_OTHER = 5;
export const TP_FILE = 6;

export const ACCOUNT_FLAG_MNI = 'mni';
export const ACCOUNT_MNI_PREFIX_MERD = 'MERD';

export const AGENCY_FLAG_ADVERTISER = 'advertiser';
export const AGENCY_FLAG_DASHBOARD = 'dashboard';

// localStorage key name for accountId
export const KEY_ACCOUNT_ID = 'accountId';

// Statuses
export const LIS_DRAFT = 1;
export const LIS_NEW = 2;
export const LIS_PENDING_ACTIVATION = 3;
export const LIS_ACTIVATED = 4;
export const LIS_CAMPAIGN_LIVE = 5;
export const LIS_PAUSED = 7;
export const LIS_DISAPPROVED = 8;
export const LIS_ENDED = 9;
export const LIS_REOPENED = 29;
export const LIS_CANCELED = 10;
export const LIS_PENDING_CANCELLATION = 23;
export const LIS_PENDING_PAUSED = 24;

export const LI_MNI_CLIENT_EDITABLE_STATUS = [LIS_DRAFT, LIS_DISAPPROVED];

export const LIS_CHANGE_DRAFT = 19;
export const LIS_CHANGE_NEW = 20;
export const LIS_CHANGE_PENDING_ACTIVATION = 21;
export const LIS_CHANGE_APPLIED = 22;

export const KPI_AWARENESS = 10;
export const KPI_FLIP_PRIMARY = 11;
export const KPI_FLIP_SECONDARY = 12;

export const EMAIL_NOTIFICATION_CATEGORY = {
  [LIS_PENDING_ACTIVATION]: 'LI_READY_FOR_ACTIVATION',
  [LIS_ACTIVATED]: 'LI_ACTIVATED',
  [LIS_CAMPAIGN_LIVE]: 'LI_CAMPAIGN_LIVE',
  [LIS_PAUSED]: 'LI_PAUSED',
  [LIS_CANCELED]: 'LI_CANCELED',
  [LIS_CHANGE_PENDING_ACTIVATION]: 'LI_CHANGE_READY',
  [LIS_CHANGE_APPLIED]: 'LI_CHANGE_APPLIED',
};

// Email notification category "DISAPPROVED"
export const ENC_DISAPPROVED = 'LI_DISAPPROVED';

export const IOS_DRAFT = 12;
export const IOS_NEW = 11;
export const IOS_COMPLETED = 13;
export const IOS_LIVE = 14;

export const IOS_CHANGE_DRAFT = 15;
export const IOS_CHANGE_NEW = 16;
export const IOS_CHANGE_READY_FOR_ACTIVATION = 17;
export const IOS_CHANGE_APPLIED = 18;
export const IOS_CHANGE_REQUEST_TO_CANCEL = 27;
export const IOS_CHANGE_CANCELED = 28;

export const LIT_CHANGE = 'CHANGE';
export const LIT_LINE = 'LINE';

export const IO_STATE_CHANGE = 'CHANGE';

export const TEOB_HOMEOWNERS = 1;
export const TEOB_AUTO_INTENDERSS = 2;

/**
 * Map of statuses upon which we trigger automatic changes to line items.
 */
export const DRAFT_STATUS_CHANGE_MAP = {
  [IOS_DRAFT]: [LIS_NEW, LIS_DRAFT],
  [IOS_CHANGE_DRAFT]: [LIS_CHANGE_NEW, LIS_CHANGE_DRAFT],
};
// Object.keys returns string names, need to cast back to int here
DRAFT_STATUS_CHANGE_MAP.ids = Object.keys(DRAFT_STATUS_CHANGE_MAP).map((k) =>
  Number.parseInt(k, 10)
);

export const ACCOUNT_TYPE_MNI = 'mni';
export const ACCOUNT_TYPE_CORE = 'core';

// permissions constants.
export const RATE_CARD_WRITE = 'rate_card_write';
export const LI_STATUS_FOR_CR_ENABLED = [
  LIS_ACTIVATED,
  LIS_CAMPAIGN_LIVE,
  LIS_PENDING_PAUSED,
  LIS_PAUSED,
  LIS_DISAPPROVED,
  LIS_PENDING_CANCELLATION,
  LIS_REOPENED,
];

// LI Workflows
export const LIW_NEW_AM = 1;
export const LIW_NEW_ACT = 2;
export const LIW_NEW_CHANGE_AM = 3;
export const LIW_NEW_CHANGE_ACT = 4;
export const LIW_UPDATED_AM = 5;
export const LIW_QA_ACT = 6;
export const LIW_PENDING_ACT = 7;
export const LIW_PENDING_AM = 8;
export const LIW_ACTIVATED = 9;

export const ROLE_AM = 'ACCOUNT_MANAGER';
export const ROLE_ACCOUNT_ADMIN = 'ACCOUNT_ADMIN';
export const ROLE_CA = 'CLIENT_ADMIN';
export const ROLE_CM = 'CLIENT_MANAGER';
export const ROLE_CU = 'CLIENT_USER';
export const ROLE_INTERNAL_ADMIN = 'INTERNAL_ADMIN';
export const ROLE_INTERNAL_SUB_ADMIN = 'INTERNAL_SUB_ADMIN';
export const ROLE_INTERNAL_USER = 'INTERNAL_USER';
export const ROLE_EXTERNAL_SUB_ADMIN = 'EXTERNAL_SUB_ADMIN';
export const ROLE_EXTERNAL_USER = 'EXTERNAL_USER';
export const ROLE_EXTERNAL_ADMIN = 'EXTERNAL_ADMIN';

export const EXTERNAL_MNI_ACCOUNT_ID = 76;

export const IO_SOURCE_ADREADY = 'ADREADY';
export const IO_SOURCE_FLIPPLUS = 'FLIPPLUS';
export const IO_SOURCE_DMM = 'DMM';
export const IO_SOURCE_VIA = 'VIA';
